/**
 * A costura entre as duas bases de tempo do corpo (`ADR-0017`).
 *
 * O `ADR-0017` deixou isto escrito como o que faltava: "a troca de base de tempo é um
 * SALTO. Sair do previsto para o interpolado move o objeto de `RTT/2 + buffer` de uma
 * vez." Este arquivo é o que transforma esse salto em VELOCIDADE.
 *
 * A regra, inteira:
 *
 *   No frame em que o corpo troca de base, guarde a diferença entre onde ele foi
 *   DESENHADO e onde a base nova o coloca. Desenhe a base nova mais essa diferença, e
 *   deixe a diferença ir a zero em `BUD-body-handoff`.
 *
 * Três coisas caem disso, e são elas que o gate cobra:
 *
 * 1. **O caminho desenhado NEVER tem descontinuidade.** No instante da costura a
 *    diferença vale exatamente o salto, então o primeiro frame da base nova cai em
 *    cima do último frame da base velha. É a definição de não pular.
 * 2. **A mentira TERMINA.** Passada a janela, o desenhado É a base, sem resto. Um
 *    deslocamento que sobrevivesse seria pior que o salto: o objeto ficaria para
 *    sempre num lugar onde a autoridade NEVER o pôs.
 * 3. **Piscar de base NEVER acumula.** A diferença é sempre medida contra o último
 *    DESENHADO, então trocar de base a cada frame produz um caminho contínuo em vez
 *    de uma soma de correções.
 *
 * Puro de propósito, como o `body-authority.ts`: sem three, sem socket, sem relógio
 * próprio. O tempo e a decisão de base entram os dois por parâmetro.
 *
 * O que este arquivo NEVER faz: esconder erro de predição. Se o palpite estiver errado,
 * ele continua errado — a mistura só existe no INSTANTE em que a base muda, que é o
 * único momento em que o pulo NEVER é culpa de ninguém.
 */
import { INVARIANT_BUDGETS } from '@game/gamedata/perf';

/** Em quanto tempo a diferença da costura vai a zero. Invariante 5: vem do orçamento. */
const WINDOW_MS = INVARIANT_BUDGETS['BUD-body-handoff'].value;

/** O que a costura escreve. Estrutural: é o corpo que já vai para o render. */
export interface DrawnBody {
  id: number;
  x: number;
  z: number;
}

/** Memória por corpo. `drawn` é o que FOI DESENHADO, e é dele que sai a diferença. */
interface Seam {
  /** `true` quando o corpo veio pelo palpite local neste frame. */
  predicted: boolean;
  /** Falso até o primeiro frame: corpo que acabou de entrar NEVER tem de onde vir. */
  drawnOnce: boolean;
  drawnX: number;
  drawnZ: number;
  offsetX: number;
  offsetZ: number;
  leftMs: number;
  /** Última vez que este corpo apareceu. Corpo que sumiu NEVER fica ocupando memória. */
  seenAt: number;
}

export class BodyHandoff {
  private readonly seams = new Map<number, Seam>();
  private stamp = 0;
  private armed = 0;
  private lastUnits = 0;
  /**
   * Quem quer saber de CADA costura, e NEVER só do total.
   *
   * O contador responde "aconteceu quantas vezes"; o ouvinte responde "de quanto
   * foi cada uma". É a segunda pergunta que decide `BUD-body-handoff`: 200 ms é
   * hipótese, e para virar medida é preciso a DISTRIBUIÇÃO dos saltos — uma
   * costura de 3 unidades e trinta de 0,02 dão a mesma média e pedem consertos
   * opostos.
   */
  private onSeam:
    | ((bodyId: number, seamUnits: number, toPredicted: boolean, windowMs: number) => void)
    | undefined;

  /** Instala o ouvinte de costura. Um só: quem observa é a caixa-preta. */
  watchSeams(
    listener: (bodyId: number, seamUnits: number, toPredicted: boolean, windowMs: number) => void,
  ): void {
    this.onSeam = listener;
  }

  /** Quanto da janela ainda falta para o corpo dado. Zero = costura quitada. */
  pendingMs(id: number): number {
    const seam = this.seams.get(id);
    return seam === undefined ? 0 : seam.leftMs;
  }

  /**
   * Costuras armadas na sessão. Acumula de propósito, como os `solavancos` do
   * interpolador: em aparelho real o que interessa é "aconteceu quantas vezes", e
   * NEVER "está acontecendo agora".
   */
  get seamCount(): number {
    return this.armed;
  }

  /**
   * Tamanho da ÚLTIMA costura, em unidades de mundo. É exatamente o salto que o
   * jogador teria visto se esta classe NEVER existisse — e é o número que decide se
   * `BUD-body-handoff` está certo, porque é ele dividido pela janela que vira a
   * velocidade extra na tela.
   */
  get lastSeamUnits(): number {
    return this.lastUnits;
  }

  /** Maior janela ainda por quitar. Acima de zero, tem costura em curso na tela. */
  get settlingMs(): number {
    let worst = 0;
    for (const seam of this.seams.values()) {
      if (seam.leftMs > worst) worst = seam.leftMs;
    }
    return worst;
  }

  /**
   * Escreve nos corpos a posição com a costura aplicada, e lembra o que desenhou.
   * Uma vez por frame, com o delta do laço de render.
   *
   * `predictedId` é o corpo que ESTE frame desenhou pelo palpite local — 0 quando
   * nenhum. É o único sinal de base que a costura precisa: todo o resto veio do
   * passado interpolado.
   *
   * O tempo entra por aqui, e NEVER por um `advance` separado, porque a ordem entre os
   * dois é a diferença entre funcionar e congelar: ver o comentário do decaimento.
   * Buraco de aba em segundo plano NEVER precisa de guarda — um delta gigante quita a
   * costura inteira, que é o certo: o jogador voltou para uma tela que já devia ter
   * assentado, e glissar por cima disso seria o objeto se mexendo sozinho antes de ele
   * encostar no controle.
   */
  apply(bodies: readonly DrawnBody[], predictedId: number, deltaMs: number): void {
    this.stamp += 1;

    for (const body of bodies) {
      const seam = this.seamFor(body.id);
      const predicted = body.id === predictedId;

      // A costura NEVER nasce no primeiro frame de um corpo: sem passado desenhado,
      // "de onde ele veio" NEVER existe, e inventar uma diferença aqui faria todo
      // corpo que entra na tela deslizar até o lugar dele.
      if (seam.drawnOnce && seam.predicted !== predicted) {
        seam.offsetX = seam.drawnX - body.x;
        seam.offsetZ = seam.drawnZ - body.z;
        seam.leftMs = WINDOW_MS;
        this.armed += 1;
        this.lastUnits = Math.hypot(seam.offsetX, seam.offsetZ);
        try {
          this.onSeam?.(body.id, this.lastUnits, predicted, WINDOW_MS);
        } catch {
          // Observador NEVER derruba o desenho. Este método roda por FRAME.
        }
      }
      seam.predicted = predicted;

      if (seam.leftMs > 0) {
        // O tempo do frame passa AQUI, e passa TAMBÉM no frame em que a costura nasce.
        //
        // MEDIDO, e é o bug que este `Math.max` fecha: quitando só a partir do frame
        // SEGUINTE, um sinal de base que oscilasse a cada frame rearmaria a janela
        // antes de ela quitar coisa alguma, e o corpo ficaria CONGELADO na tela
        // enquanto as duas bases andavam sem ele. Contínuo e parado é pior que o salto.
        //
        // Decaimento LINEAR no tempo: o salto vira uma velocidade constante por
        // `BUD-body-handoff`, e velocidade constante é o que NEVER chama atenção. Uma
        // curva com cauda deixaria um resto que só some no infinito, e resto é o item
        // 2 desta classe sendo violado devagar.
        seam.leftMs = Math.max(0, seam.leftMs - deltaMs);
        const factor = seam.leftMs / WINDOW_MS;
        body.x += seam.offsetX * factor;
        body.z += seam.offsetZ * factor;
      }

      seam.drawnOnce = true;
      seam.drawnX = body.x;
      seam.drawnZ = body.z;
      seam.seenAt = this.stamp;
    }

    this.forgetMissing();
  }

  private seamFor(id: number): Seam {
    const existing = this.seams.get(id);
    if (existing !== undefined) return existing;
    const fresh: Seam = {
      predicted: false,
      drawnOnce: false,
      drawnX: 0,
      drawnZ: 0,
      offsetX: 0,
      offsetZ: 0,
      leftMs: 0,
      seenAt: this.stamp,
    };
    this.seams.set(id, fresh);
    return fresh;
  }

  /**
   * Esquece corpo que sumiu da tela.
   *
   * NEVER é higiene por higiene: guardado, o corpo que volta traria a diferença de
   * quando saiu, e apareceria deslizando a partir de um lugar de outra época. Sair da
   * tela e voltar tem que ser igual a entrar pela primeira vez.
   */
  private forgetMissing(): void {
    for (const [id, seam] of this.seams) {
      if (seam.seenAt !== this.stamp) this.seams.delete(id);
    }
  }
}
